'use client';

// CatalogueSection.tsx
import { CarousalComponent, Container } from '..';
import { H2Large } from './styleConstants';
import styles from './CatalogueSection.module.css';
import { useMemo } from 'react';
import { useModal } from '..';
import { UhAppModal } from 'components/shared/UHAppDownloadModal';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { PowerPlugCard } from './PowerPlugCard';
import { isMobileDevice } from 'utils/isMobile';
import { useIsCnOPlus } from 'hooks/pricing/useIsCnOPlus';
import usePowerPlugAvailability from 'hooks/pricing/usePowerPlugAvailability';
import { POWER_PLUG_IDS } from 'utils/powerPlugAvailability';
import { getCataloguePowerPlugsData } from './cataloguePowerPlugsData';
import { useTranslations } from 'next-intl';

export const CatalogueSection = ({ className }: { className?: string }) => {
  const t = useTranslations('powerplugs');
  const ModalClient = useModal();
  const { region: country, store: regionStore } = useRegionLocale();
  const { getValue } = useIsCnOPlus();
  const { availablePlugIds } = usePowerPlugAvailability();

  const availablePlugIdsSet = useMemo(
    () => new Set(availablePlugIds),
    [availablePlugIds],
  );

  const cnoPlusDescription = t(
    'powerplugsPage.catalogue.plugs.cycleOvulationPro.plusDescription',
  );

  const handleUHAppClick = (url: string) => {
    if (isMobileDevice()) {
      window.open(url, '_blank');
    } else {
      ModalClient.set(
        <UhAppModal
          text={t('powerplugsPage.hero.appModal.text')}
          link={url}
          image="web_v2/qr-codes/powerplugs-onelink.png"
        />,
      );
      ModalClient.show();
    }
  };

  const plugGroups = getCataloguePowerPlugsData(
    country,
    handleUHAppClick,
    t,
    regionStore ?? undefined,
  );

  return (
    <section className={[styles.root, className].filter(Boolean).join(' ')}>
      <Container>
        <H2Large>
          {t('powerplugsPage.sectionTitles.exploreAllLine1')}
          <br />
          {t('powerplugsPage.sectionTitles.exploreAllLine2')}
        </H2Large>
      </Container>
      {plugGroups.map((group) => {
        const visiblePlugs = group.plugs.filter(
          (plug) => !plug.plugId || availablePlugIdsSet.has(plug.plugId),
        );

        if (!visiblePlugs.length) {
          return null;
        }

        return (
          <div className={styles.carouselWrapper} key={group.key}>
            <CarousalComponent
              title={group.group}
              data={visiblePlugs.map((plug, idx) => {
                const titleKey = plug.plugId ?? `plug-${idx}`;
                const isCycleOvulationPremiumCard =
                  plug.plugId === POWER_PLUG_IDS.CYCLE_OVULATION_PREMIUM;

                return (
                  <PowerPlugCard
                    key={`${group.key}-${titleKey}`}
                    title={
                      isCycleOvulationPremiumCard
                        ? getValue(
                            plug.title,
                            <>
                              {t(
                                'powerplugsPage.catalogue.plugs.cycleOvulationPro.titleLine1',
                              )}{' '}
                              <br />{' '}
                              {t(
                                'powerplugsPage.catalogue.plugs.cycleOvulationPro.plusTitleLine2',
                              )}
                            </>,
                          )
                        : plug.title
                    }
                    subtitle={
                      typeof plug.subtitle === 'function'
                        ? plug.subtitle()
                        : plug.subtitle
                    }
                    description={
                      isCycleOvulationPremiumCard
                        ? getValue(plug.description, cnoPlusDescription)
                        : plug.description
                    }
                    image={plug.image}
                    alt={plug.alt}
                    style={plug.style}
                    onExplore={plug.onExplore}
                    exploreLabel={t('powerplugsPage.catalogue.explore')}
                  />
                );
              })}
            />
          </div>
        );
      })}
    </section>
  );
};
